import React from "react";
import { BaseLayout } from "@layouts/BaseLayout";
import PageHero from "@shared/InnerPageHero";
import GetMeta from "@pages/SeoPages/GetMeta";

const AgentTermsAndCondition = () => {
  return (
    <BaseLayout>
      <GetMeta
        description="Review the terms of service for using our property listing site. Understand the guidelines, user responsibilities, and legal obligations when accessing property listings for sale, rent, and shortlets. Ensure a secure and informed experience."
        title="Terms of Service | Krent Inc"
        canonicalValue="terms"
      />

      <section className="mb-5" style={{ marginTop: "-2rem" }}>
        <PageHero img="/assets/terms.jpg" currentPage="Terms of Service" />
      </section>

      <div className="container py-4">
        <h2 className="fw-bold mb-3">KRENT TERMS AND CONDITIONS</h2>
        <p>Last Updated: November, 2025</p>

        <p>
          Welcome to Krent (“the Platform”), operated as Krent Inc. (“we,”
          “our,” or “us”). By accessing or using our Platform, you (“you,”
          “user,” or “Agent/Developer”) agree to be bound by these Terms and
          Conditions and our Privacy Policy. If you do not agree, please do not
          use this Platform.
        </p>

        <h4 className="fw-bold mt-4">1. Acceptance of Terms</h4>
        <p>
          By registering, accessing, or using this Platform, you confirm that
          you have read, understood, and agree to comply with these terms as
          Krent Agent. These terms govern all categories of Krent Agents,
          including Property Verification Agents, Developer Agents, and
          participants in the Platform Referral Program. If you are using this
          Platform on behalf of a company or organization, you represent that
          you have the authority to bind that entity to these Terms.
        </p>

        <h4 className="fw-bold mt-4">2. Definitions</h4>
        <p>Platform: Refers to the Krent website, app, or system.</p>
        <p>
          Agent: Standard agent performing general platform tasks, client
          management, and lead handling.
        </p>
        <p>
          Developer Agent: Agents responsible for accompanying clients for
          property inspections, uploading KYC, and official Krent tasks.
        </p>
        <p>
          Referrer: Agents responsible for onboarding property owners, agents,
          or renters using official Krent links or codes.
        </p>
        <p>
          Clients/Leads: Individuals or organizations obtained or managed
          through the Platform.
        </p>
        <p>Intellectual Property: Proprietary rights owned by Krent.</p>

        <h4 className="fw-bold mt-4">3. Eligibility</h4>
        <ul>
          <li>Be at least 18 years old;</li>
          <li>Provide accurate and complete registration information;</li>
          <li>Complete necessary verification processes;</li>
          <li>Comply with all applicable laws.</li>
        </ul>

        <h4 className="fw-bold mt-4">4. Use of Platform</h4>
        <p>
          You agree to use this Platform only for lawful purposes. You must not:
        </p>
        <ul>
          <li>Engage in fraudulent or misleading activities;</li>
          <li>
            Copy, modify, or distribute any part of the Platform without
            permission;
          </li>
          <li>Attempt unauthorized access to the Platform or its data.</li>
        </ul>

        <h4 className="fw-bold mt-4">
          5. Agent Duties with Krent Operational Ethics
        </h4>
        <ul>
          <li>
            Maintain professionalism and integrity with property owners,
            tenants, and clients.
          </li>
          <li>Accurately represent Krent and its services.</li>
          <li>Protect confidential and proprietary information.</li>
          <li>Follow company guidelines and procedures.</li>
        </ul>

        <h5 className="fw-bold mt-3">5.1 Property Verification Ethics</h5>
        <ul>
          <li>Provide accurate and truthful information during inspections.</li>
          <li>Complete submissions using the official Krent platform.</li>
        </ul>

        <h5 className="fw-bold mt-3">5.2 Developer Agent Responsibilities</h5>
        <ul>
          <li>Accompany clients only for official Krent tasks.</li>
          <li>Upload KYC, feedback, and confirmations promptly.</li>
          <li>Do not negotiate or collect payments outside Krent.</li>
        </ul>

        <h5 className="fw-bold mt-3">5.3 Marketing and Referrals</h5>
        <ul>
          <li>Share only official Krent links or codes.</li>
          <li>Commissions are earned only for verified transactions.</li>
          <li>No bypassing Krent for personal fees.</li>
        </ul>

        <h4 className="fw-bold mt-4">6. Exclusivity Clause</h4>
        <p>
          All Leads, Clients, data, and materials from the Platform belong to
          Krent. Agents shall not divert, share, or use leads for personal or
          third-party benefit. Violations may lead to suspension or legal
          action.
        </p>

        <h4 className="fw-bold mt-4">7. Intellectual Property</h4>
        <p>
          All content, logos, and systems are owned by Krent. Users are granted
          a limited, revocable license for intended use only.
        </p>

        <h4 className="fw-bold mt-4">8. Payment and Fees</h4>
        <ul>
          <li>All payments must be processed through Krent.</li>
          <li>Agents cannot collect payments outside the platform.</li>
          <li>
            Bypassing the platform may lead to suspension or legal action.
          </li>
        </ul>

        <h4 className="fw-bold mt-4">9. Confidentiality</h4>
        <p>
          Agents must maintain strict confidentiality of client data, property
          details, and transaction information.
        </p>

        <h4 className="fw-bold mt-4">10. Termination</h4>
        <ul>
          <li>Violating the Terms.</li>
          <li>Misusing company resources.</li>
          <li>Fraudulent or unethical conduct.</li>
          <li>Repeated negligence.</li>
        </ul>
        <p>
          Upon termination, all Krent materials must be deleted or returned.
        </p>

        <h4 className="fw-bold mt-4">11. Limitation of Liability</h4>
        <p>
          Krent is not liable for any damages resulting from your use or
          inability to use the Platform.
        </p>

        <h4 className="fw-bold mt-4">12. Indemnification</h4>
        <p>
          You agree to indemnify Krent against claims arising from misuse of the
          Platform or violation of the Terms.
        </p>

        <h4 className="fw-bold mt-4">13. Modifications to Terms</h4>
        <p>
          Krent may modify these Terms at any time. Continued use constitutes
          acceptance.
        </p>

        <h4 className="fw-bold mt-4">14. Governing Law</h4>
        <p>
          These Terms are governed by Nigerian law. Disputes will be resolved in
          Nigerian courts.
        </p>

        <h4 className="fw-bold mt-4">15. Contact Us</h4>
        <p>📧 support.krent.inc@gmail.com</p>
        <p>🌐 www.krentinc.com</p>
        <p>
          📍 Bayview Estate, Chisco Bus-stop, Lekki Ajah Expressway, Lagos
          State.
        </p>
        <p>📞 091800800808</p>
      </div>
    </BaseLayout>
  );
};

export default AgentTermsAndCondition;
